import React, { useEffect, useState, memo, useCallback, useMemo } from 'react';
import { ChartSettingsT, VizType } from 'business/definitions';
import StatusManager from 'components/StatusManager';
import { sortBy, isEqual } from 'lodash';
import { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { HttpError } from 'store/APIClient';
import { useBackendSyncedContext } from 'routes/Explore/components/Contexts/BackendSyncedContext/BackendSyncedContext';
import { useSettingsDispatchContext } from 'routes/Explore/components/Contexts/SettingsContext/SettingsContext';
import { useTranslation } from 'react-i18next';
import Chart from './components/Chart/Chart';
import RawDataModal from './components/RawDataModal/RawDataModal';

import Breadcrumbs from './components/Breadcrumbs/Breadcrumbs';

import { BreadCrumbNodeT, GetDataMakerResponse } from './types';
import { useGetExtraFilters } from './hooks/extraFilters';
import { useGetNavigationTree } from './hooks/navigationTree';
import { getQueryExtraFilters } from './helpers/extraFilters';
import { useRollbackBreadcrumbs } from './hooks/breadcrumbs';
import { getInitialBreadcrumbs } from './helpers/breadcrumbs';
import { useOnChartclick } from './hooks/onChartClick';
import {
    ChartContext,
    ChartContextValue,
    useChartContext,
} from './context/ChartContext';
import { getChartStatus } from './helpers/chartStatus';
import {
    createParentChartDataMakerPayload,
    prefixParentDataLabel,
} from './helpers/parentDataHelper';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';

export type ChartDataQueryPayload = {
    chartKey: number;
    queryMetrics: QueryMetricT[];
    queryDimensions: QueryDimensionT[];
    queryFilters?: QueryFilterT[];
    // extra filters are dashboardFilters + navigationFilter
    extraFilters: QueryFilterT[];
    cacheKey: QueryKey;
    publicToken?: string;
    enabled: boolean;
};

type Props = {
    applyChartSettings: (settings: ChartSettingsT) => void;
    isPngModeActivated: boolean;
    isPdfModeActivated: boolean;
    onChartDataChange?: (data: GetDataMakerResponse) => void;
    settings: ChartSettingsT;
    title: string;
    useChartDataMaker: (
        payload: ChartDataQueryPayload,
        onError?: (err: HttpError) => void,
        onSuccess?: (data: GetDataMakerResponse) => void,
    ) => UseQueryResult<GetDataMakerResponse, HttpError>;
    vizType: VizType;
    isFromArchivedDashboard?: boolean;
};

const ChartContainer = ({
    applyChartSettings,
    isPngModeActivated,
    isPdfModeActivated,
    onChartDataChange = () => {},
    settings,
    title,
    useChartDataMaker,
    vizType,
    isFromArchivedDashboard = undefined,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const {
        queryDimensions,
        queryFilters,
        queryMetrics,
        disableDashboardFilters,
        publicToken,
        isChartViewedOnDashboard,
        chartKey,
        isUpdateChartLoading,
    } = useChartContext();
    const dashboard = useGetDashboardFromSlugOrMetadata();
    const { onQuerySucceeded } = useSettingsDispatchContext();
    const [breadcrumbs, setBreadcrumbs] = useState<BreadCrumbNodeT[]>(
        getInitialBreadcrumbs(t),
    );

    const [navigationFilter, setNavigationFilter] = useState<
        QueryFilterT | undefined
    >(undefined);

    const [selectedPoint, setSelectedPoint] = useState<
        ResponseDataT | undefined
    >(undefined);

    const [chartHasError, setChartHasError] = useState(false);

    const { isBackendSynced } = useBackendSyncedContext();

    const { data: allExtraFilters, isLoading: isExtraFiltersLoading } =
        useGetExtraFilters(
            disableDashboardFilters,
            dashboard,
            navigationFilter,
        );

    const queryExtraFilters = getQueryExtraFilters(
        navigationFilter,
        allExtraFilters,
        disableDashboardFilters,
        isChartViewedOnDashboard,
        isBackendSynced,
    );

    const cacheKey = useMemo(
        () => [
            sortBy(queryMetrics, (m) => m.field),
            sortBy(queryFilters, (f) => f.field),
            sortBy(queryDimensions, (d) => d.field),
            sortBy(allExtraFilters, (ef) => ef.field),
        ],
        [queryMetrics, queryFilters, queryDimensions, allExtraFilters],
    );

    const chartDataMakerPayload = {
        chartKey,
        queryMetrics,
        queryDimensions,
        queryFilters,
        extraFilters: queryExtraFilters,
        cacheKey,
        publicToken,
        enabled: !isUpdateChartLoading && !isExtraFiltersLoading,
    };

    const { data, error, isLoading, isError } = useChartDataMaker(
        chartDataMakerPayload,
    );

    const parentChartDataMakerPayload = createParentChartDataMakerPayload(
        chartDataMakerPayload,
        isUpdateChartLoading,
        breadcrumbs,
    );

    const { data: parentData, isLoading: isParentDataLoading } =
        useChartDataMaker(parentChartDataMakerPayload);

    const { data: parentResponseData } = parentData ?? {
        data: [],
        columns: [],
    };

    useEffect(() => {
        if (data) onChartDataChange(data);
    }, [data, onChartDataChange]);

    const { data: navigationTrees } = useGetNavigationTree(
        chartKey,
        queryDimensions,
    );

    const hideDetailsModal = () => {
        setSelectedPoint(undefined);
    };

    const { data: responseData, columns: responseColumns } = data ?? {
        data: [],
        columns: [],
    };

    const onChartClick = useOnChartclick(
        breadcrumbs,
        navigationTrees,
        queryFilters,
        setBreadcrumbs,
        setNavigationFilter,
        setSelectedPoint,
        vizType,
        isFromArchivedDashboard,
    );

    const rollbackBreadcrumb = useRollbackBreadcrumbs(
        breadcrumbs,
        navigationTrees,
        queryFilters,
        setBreadcrumbs,
        setNavigationFilter,
        t,
    );

    const breadcrumbsComponents = (
        <Breadcrumbs breadcrumbs={breadcrumbs} onClick={rollbackBreadcrumb} />
    );
    // NOTE: this useEffect's role is to replace a 'onSuccess' clause on the
    // useGetExploreChartData hook, because if the data is already cached (which is the case
    // when we navigate from dashboard to explore) the onSuccess is not called and we need
    // this function to be called in order to get availableViz
    useEffect(() => {
        if (
            !isChartViewedOnDashboard &&
            !isExtraFiltersLoading &&
            !isLoading &&
            !isParentDataLoading &&
            !isError
        ) {
            onQuerySucceeded({
                data: [...responseData, ...parentResponseData],
                columns: responseColumns,
                queryMetrics,
                queryDimensions,
            });
        }
    }, [
        onQuerySucceeded,
        isChartViewedOnDashboard,
        isError,
        isLoading,
        isParentDataLoading,
        isExtraFiltersLoading,
        queryDimensions,
        queryMetrics,
        JSON.stringify(responseColumns),
        JSON.stringify(responseData),
        JSON.stringify(parentResponseData),
    ]);

    useEffect(() => {
        if (!isParentDataLoading && !isLoading && !isExtraFiltersLoading) {
            prefixParentDataLabel(
                breadcrumbs,
                parentResponseData,
                responseData,
                t('chart.parentPrefix'),
            );
        }
    }, [isParentDataLoading, isLoading, isExtraFiltersLoading]);

    const onFormatVizDataFailed = useCallback(
        () => setChartHasError(true),
        [setChartHasError],
    );
    const chartStatus = getChartStatus(
        queryMetrics.length > 0,
        chartHasError,
        isLoading || isParentDataLoading || isExtraFiltersLoading,
        error,
    );
    return (
        <StatusManager
            chartStatus={chartStatus}
            disableLazyLoading={isPngModeActivated || isPdfModeActivated}
            dataLength={responseData.length + parentResponseData.length}
            breadcrumbs={breadcrumbsComponents}
        >
            <Chart
                chartKey={chartKey}
                vizType={vizType}
                queryMetrics={queryMetrics}
                queryDimensions={queryDimensions}
                queryFilters={queryFilters}
                settings={settings}
                applyChartSettings={applyChartSettings}
                showDetailsModal={onChartClick}
                data={[...parentResponseData, ...responseData]}
                columns={responseColumns}
                isPngModeActivated={isPngModeActivated}
                onFormatVizDataFailed={onFormatVizDataFailed}
                isChartViewedOnDashboard={isChartViewedOnDashboard}
                timeFilters={data?.timeFilters}
            />
            {Boolean(selectedPoint) && (
                <RawDataModal
                    chartTitle={title}
                    closeModal={hideDetailsModal}
                    columns={responseColumns}
                    dimensions={queryDimensions}
                    extraFilters={allExtraFilters}
                    filters={queryFilters}
                    metrics={queryMetrics}
                    point={selectedPoint}
                    show={Boolean(selectedPoint)}
                />
            )}
        </StatusManager>
    );
};

const ChartContainerWithContext = ({
    chartKey,
    queryDimensions,
    queryFilters,
    queryMetrics,
    publicToken,
    isUpdateChartLoading,
    isChartViewedOnDashboard,
    disableDashboardFilters = false,
    ...rest
}: Props & ChartContextValue) => (
    <ChartContext.Provider
        value={{
            chartKey,
            queryMetrics,
            queryFilters,
            queryDimensions,
            publicToken,
            isUpdateChartLoading,
            isChartViewedOnDashboard,
            disableDashboardFilters,
        }}
    >
        <ChartContainer {...rest} />
    </ChartContext.Provider>
);

export default memo(ChartContainerWithContext, isEqual);
